import { useRef, useState, useEffect } from 'react';

const useAgGrid = (props = {}) => {
  const {
    filterModel,
    showFilters,
    isMobile
  } = props;

  // setup handles for AG Grid
  const gridApi = useRef();
  const setGridApi = api => {
    gridApi.current = api;
  }

  const columnApi = useRef();
  const setColumnApi = api => {
    columnApi.current = api;
  }

  // setup filters active flag.
  // this needs to check for updates to the showFilters prop
  // to enable deeplinking via URL (on first render it will be false)
  const filtersActiveRef = useRef(showFilters || false);
  const setFiltersActive = bool => {
    filtersActiveRef.current = bool;
  }

  useEffect(() => {
    if (!filtersActiveRef.current && showFilters) {
      setFiltersActive(true);
    }
  }, [showFilters]);

  const [selectedRows, setSelectedRows] = useState([]);

  const gridRef = useRef();
  const headerRef = useRef();
  useEffect(() => {
    if (!gridRef.current) {
      gridRef.current = document.querySelector('.grid-container');
    }

    if (!headerRef.current) {
      headerRef.current = document.querySelector('.ag-header');
    }
  });

  const setHeaderHeight = () => {
    // set header height as needed for filter stacks.
    // note that setHeaderHeight sets AgGrid's internal height setting,
    // which won't match the actual height,
    // where as the headerRef height sets the height on the DOM directly
    if (filtersActiveRef.current) {
      let filterRows = Object.keys(filterModel || {})
        .map(field => filterModel[field]?.length || 0)
        .sort();

      let filterRowCount = filterRows[filterRows.length - 1] || 1;

      // normally setting the header height sets the height for a row,
      // with AgGrid assuming that there will be 2 rows, one for the headers
      // and another for the filters.
      // in reality we want to keep the first row (headers) pinned at 48px
      // and adjust the height of the second row (filters) to contain
      // the filter stacks.
      // so this headerHeight value isn't the actual header height,
      // it's just a value for AgGrid that will result in the height that we want.
      // this works out to 43px + 23px for each filter row
      // (after 2 rows 24px would be best)
      let multiplier = filterRowCount > 2 ? 24 : 23;
      let headerHeight = 43 + (filterRowCount * multiplier);
      gridApi.current?.setHeaderHeight(headerHeight);

      if (gridRef.current) {
        gridRef.current.classList.remove('ag-filters-collapse');
      }

    } else {
      gridApi.current?.setHeaderHeight(48);

      if (gridRef.current) {
        gridRef.current.classList.add('ag-filters-collapse');
      }
    }

  }

  useEffect(setHeaderHeight);

  const getHeaderHeight = () => headerRef.current?.style.height;

  const getSelectedRows = () => {
    return gridApi.current.getSelectedRows();
  }

  const onSelectionChanged = () => {
    const selectedRows = getSelectedRows();
    setSelectedRows(selectedRows);
  }

  const updateRow = newData => {
    gridApi.current.forEachNode(rowNode => {
      if (rowNode.data?.id === newData.id) {
        rowNode.setData(newData);
      }
    });
  }

  const refreshGrid = () => {
    gridApi.current?.rowModel.onFilterChanged();
  }

  const setGridOption = (dataSource, option) => {
    gridApi.current.setDatasource(dataSource.getDataSource);
  }

  const refreshHeader = () => {
    if (gridApi.current) {
      gridApi.current.refreshHeader();
    }
  }

  const refreshCells = () => {
    if (gridApi.current) {
      gridApi.current.refreshCells({ force: false, suppressFlash: false });
    }
  }

  const setDataSource = getDataSource => {
    if (gridApi.current) {
      gridApi.current.setDatasource(getDataSource);
    }
  }

  const toggleFilters = () => {
    if (filtersActiveRef.current) {
      // hide and clear filters
      setFiltersActive(false);
    } else {
      // show filters
      setFiltersActive(true);
    }

    setHeaderHeight();
  }

  const sizeColumnsToFit = () => {
    if (gridApi.current && !isMobile) {
      gridApi.current.sizeColumnsToFit();
    }
  }

  const getAllColumns = () => {
    return columnApi.current.getAllColumns();
  }

  const autoSizeColumns = columns => {
    columnApi.current.autoSizeColumns(columns);
  }

  const getRenderedColumnDefs = () => {
    let colDefs = getAllColumns().map(col => col.colDef)
      .filter(colDef => colDef.visible)

    let pinnedCols = colDefs.filter(colDef => colDef.pinned === 'left');

    return pinnedCols.concat(colDefs.filter(colDef => colDef.pinned !== 'left'));
  }

  return {
    gridApi,
    setGridApi,
    columnApi,
    setColumnApi,
    selectedRows,
    onSelectionChanged,
    updateRow,
    refreshGrid,
    setGridOption,
    refreshHeader,
    refreshCells,
    setDataSource,
    filtersActiveRef,
    toggleFilters,
    sizeColumnsToFit,
    getAllColumns,
    autoSizeColumns,
    getRenderedColumnDefs,
    setHeaderHeight,
    getHeaderHeight
  }
}

export default useAgGrid;
